import React from 'react';
import { motion } from 'framer-motion';

/**
 * Dotted world map (original, hand-laid). Continents are approximated by
 * a deterministic dot field; major GEO markers + curved route lines float on top.
 * Pure SVG — no external assets.
 */
const CONTINENTS = [
  // [cx, cy, rx, ry, density 0..1]
  { cx: 165, cy: 175, rx: 92, ry: 60, n: 90 },   // North America
  { cx: 235, cy: 320, rx: 48, ry: 70, n: 70 },   // South America
  { cx: 470, cy: 175, rx: 60, ry: 48, n: 75 },   // Europe
  { cx: 510, cy: 290, rx: 70, ry: 80, n: 90 },   // Africa
  { cx: 700, cy: 215, rx: 130, ry: 90, n: 160 }, // Asia
  { cx: 800, cy: 360, rx: 50, ry: 28, n: 38 },   // Australia
];

// GEO markers: [code, x, y, color]
const GEO_POINTS = [
  ['US', 175, 195, '#22D3EE'],
  ['BR', 245, 320, '#34D399'],
  ['DE', 470, 165, '#3B82F6'],
  ['NG', 495, 270, '#8B5CF6'],
  ['AE', 580, 220, '#FBBF24'],
  ['IN', 660, 230, '#22D3EE'],
  ['ID', 760, 305, '#34D399'],
  ['PH', 790, 260, '#8B5CF6'],
];

// Animated routes (q-curve between two GEOs)
const ROUTES = [
  { from: 'US', to: 'DE', color: '#22D3EE' },
  { from: 'DE', to: 'IN', color: '#3B82F6' },
  { from: 'IN', to: 'ID', color: '#34D399' },
  { from: 'AE', to: 'NG', color: '#8B5CF6' },
  { from: 'BR', to: 'NG', color: '#FBBF24' },
  { from: 'PH', to: 'IN', color: '#22D3EE' },
];

function pointBy(code) {
  return GEO_POINTS.find((p) => p[0] === code);
}

function seedRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function generateDots() {
  const dots = [];
  CONTINENTS.forEach((c, i) => {
    const rand = seedRandom(101 + i * 17);
    for (let k = 0; k < c.n; k++) {
      // Random within ellipse
      const t = rand() * Math.PI * 2;
      const r = Math.sqrt(rand());
      const x = c.cx + Math.cos(t) * c.rx * r;
      const y = c.cy + Math.sin(t) * c.ry * r;
      dots.push([x, y]);
    }
  });
  return dots;
}

const DOTS = generateDots();

export default function WorldMap({ className = '', showLabels = true, intensity = 'normal' }) {
  return (
    <div className={`relative w-full h-full ${className}`} data-testid="world-map">
      <svg viewBox="0 0 1000 500" preserveAspectRatio="xMidYMid meet" className="w-full h-full" aria-hidden>
        <defs>
          <linearGradient id="wm-line" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0" />
            <stop offset="50%" stopColor="#22D3EE" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="wm-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.55)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </radialGradient>
          <filter id="wm-blur">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
        </defs>

        {/* Continent dot field */}
        <g filter="url(#wm-blur)">
          {DOTS.map(([x, y], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={1.4}
              fill={i % 17 === 0 ? '#7DDCFF' : '#3F5572'}
              opacity={i % 17 === 0 ? 0.9 : intensity === 'high' ? 0.55 : 0.42}
            />
          ))}
        </g>

        {/* Latitude / longitude faint lines */}
        <g stroke="#1E2C44" strokeWidth="0.4" opacity="0.55">
          {[100, 200, 300, 400].map((y) => (
            <line key={`lat-${y}`} x1="40" y1={y} x2="960" y2={y} />
          ))}
          {[200, 400, 600, 800].map((x) => (
            <line key={`lng-${x}`} x1={x} y1="40" x2={x} y2="460" />
          ))}
        </g>

        {/* Curved animated route lines */}
        {ROUTES.map((r, i) => {
          const a = pointBy(r.from);
          const b = pointBy(r.to);
          if (!a || !b) return null;
          const mx = (a[1] + b[1]) / 2;
          const my = Math.min(a[2], b[2]) - Math.abs(a[1] - b[1]) * 0.18 - 30;
          const d = `M ${a[1]} ${a[2]} Q ${mx} ${my} ${b[1]} ${b[2]}`;
          return (
            <g key={i}>
              <path d={d} fill="none" stroke={r.color} strokeOpacity="0.45" strokeWidth="1.1" />
              <motion.circle
                r="2.2"
                fill={r.color}
                style={{ filter: 'drop-shadow(0 0 6px ' + r.color + ')' }}
                initial={false}
                animate={{ offsetDistance: ['0%', '100%'] }}
                transition={{ duration: 5 + i * 0.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                // offsetPath is set via attr below for browser support
                // Fallback: simple translate using path is not trivial; use opacity pulse instead
                opacity="0"
              />
            </g>
          );
        })}

        {/* GEO markers */}
        {GEO_POINTS.map(([code, x, y, color]) => (
          <g key={code}>
            {/* Outer pulse */}
            <circle cx={x} cy={y} r="10" fill={color} opacity="0.18" />
            <circle cx={x} cy={y} r="6" fill={color} opacity="0.35" className="pulse-glow" />
            <circle cx={x} cy={y} r="3" fill={color} style={{ filter: `drop-shadow(0 0 8px ${color})` }} />
            {showLabels && (
              <text
                x={x + 9}
                y={y - 7}
                fontFamily="'Space Grotesk', system-ui, sans-serif"
                fontSize="11"
                fontWeight="700"
                fill="#E6F3FF"
                opacity="0.92"
              >
                {code}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
